import { useFormContext, Controller, useFieldArray } from 'react-hook-form';

import { TotemSelect, Option } from '../TotemSelect';
import { DetailList, DetailListRemoveButton } from './DetailList';

export type { Option };

/**
 * For this component, it is mandatory to define defaultValues (if any) in the useForm.
 * The name should be the same as in the defaultValues.
 * Important : make sure name is in object shape: `name=test.0.name`
 * The objects defined in the array should have the following structure :
 * {
 *      value: "123456",
 * }
 * See https://react-hook-form.com/api/usefieldarray for more info
 * @author benjamin-vaysse
 */
export function DetailFormArrayValueWithSelect({
    name,
    label,
    sublabel,
    options,
}: {
    name: string;
    label?: string;
    sublabel?: string;
    options: Option[];
}) {
    const { control } = useFormContext();
    const { fields, append, remove } = useFieldArray({ name });

    const hasAvailableOptions = options?.some((option) => !option.isDisabled);

    const items = fields.map((field, index) => (
        <Controller
            key={field.id}
            name={`${name}.${index}.value` as const}
            control={control}
            render={({ field: { onChange, value } }) => (
                <>
                    <TotemSelect
                        className="min-w-[260px]"
                        onChange={onChange}
                        value={value ?? null}
                        options={options}
                    />
                    <DetailListRemoveButton onClick={() => remove(index)} />
                </>
            )}
        />
    ));

    return (
        <DetailList
            label={label}
            sublabel={sublabel}
            items={items}
            onAdd={
                hasAvailableOptions
                    ? () => append({ value: options?.find((option) => !option.isDisabled)?.value ?? null })
                    : undefined
            }
        />
    );
}
